import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ChatHeader from './components/ChatHeader';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
import QuickActions from './components/QuickActions';
import FloatingChatButton from './components/FloatingChatButton';

const AIChatInterface = () => {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isConnected, setIsConnected] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  
  // Mock conversation data
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: `Olá! Sou o Mentor Dai, seu assistente de aprendizagem personalizado.\n\nEstou aqui para ajudá-lo a:\n• Esclarecer conceitos difíceis\n• Criar quizzes personalizados\n• Sugerir conteúdo relacionado\n• Acompanhar seu progresso\n\nComo posso ajudá-lo hoje?`,
      timestamp: new Date(Date.now() - 300000)
    },
    {
      id: 2,
      type: 'user',
      content: 'Olá Dai! Estou com dificuldades para entender o conceito de funções em JavaScript.',
      timestamp: new Date(Date.now() - 240000)
    },
    {
      id: 3,
      type: 'ai',
      content: {
        type: 'link',
        text: `Entendo sua dificuldade! Funções em JavaScript são blocos de código reutilizáveis que executam tarefas específicas.\n\nVou explicar de forma simples:\n• Uma função é como uma "máquina" que recebe dados (parâmetros)\n• Processa esses dados\n• Retorna um resultado\n\nVou compartilhar um conteúdo que pode ajudar:`,
        url: '/course-detail-view',
        linkText: 'Ver lição: Fundamentos de JavaScript'
      },
      timestamp: new Date(Date.now() - 180000)
    },
    {
      id: 4,
      type: 'user',
      content: 'Obrigado! Pode me dar um exemplo prático?',
      timestamp: new Date(Date.now() - 120000)
    },
    {
      id: 5,
      type: 'ai',
      content: `Claro! Aqui está um exemplo prático:\n\n\`\`\`javascript\n// Função simples que soma dois números\nfunction somar(a, b) {\n  return a + b;\n}\n\n// Usando a função\nlet resultado = somar(5, 3);\nconsole.log(resultado); // 8\n\`\`\`\n\nNeste exemplo:\n• \`somar\` é o nome da função\n• \`a\` e \`b\` são os parâmetros\n• \`return a + b\` é o que a função faz\n• \`resultado\` recebe o valor retornado (8)`,
      timestamp: new Date(Date.now() - 60000)
    }
  ]);

  const currentLesson = "Fundamentos de JavaScript - Funções";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSendMessage = (content) => {
    const newMessage = {
      id: messages?.length + 1,
      type: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponses = [
        `Excelente pergunta! Deixe-me explicar isso de forma detalhada...\n\nEsse conceito é fundamental para seu aprendizado e vou quebrar em partes menores para facilitar o entendimento.`,
        {
          type: 'media',
          text: 'Aqui está um vídeo que pode ajudar a visualizar melhor este conceito:',
        },
        `Entendi seu ponto de vista. Vamos abordar isso de uma perspectiva diferente...\n\nPrimeiro, é importante entender que cada pessoa aprende de forma única, então vou adaptar minha explicação ao seu estilo.`,
        {
          type: 'link',
          text: 'Baseado na sua pergunta, recomendo este conteúdo complementar:',
          url: '/activity-interface',
          linkText: 'Atividades práticas relacionadas'
        }
      ];

      const randomResponse = aiResponses?.[Math.floor(Math.random() * aiResponses?.length)];
      
      const aiMessage = {
        id: messages?.length + 2,
        type: 'ai',
        content: randomResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 2000);
  };

  const handleQuickAction = (actionType, message) => {
    handleSendMessage(message);
  };

  const handleLoadMore = () => {
    setIsLoading(true);
    
    // Simulate loading more messages
    setTimeout(() => {
      const olderMessages = [
        {
          id: `older-${Date.now()}`,
          type: 'ai',
          content: 'Esta é uma mensagem mais antiga da nossa conversa anterior...',
          timestamp: new Date(Date.now() - 86400000) // 1 day ago
        }
      ];
      
      setMessages(prev => [...olderMessages, ...prev]);
      setIsLoading(false);
      setHasMoreMessages(false);
    }, 1500);
  };

  const handleToggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleCloseChat = () => {
    if (isMobile) {
      navigate('/dashboard-home');
    } else {
      setIsChatOpen(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Mentor Dai', path: '/ai-chat-interface-dai-mentor', isActive: true }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        {/* Mobile Full Screen Chat */}
        {isMobile && isChatOpen && (
          <div className="fixed inset-0 top-16 bg-background z-300">
            <div className="flex flex-col h-full">
              <ChatHeader 
                onClose={handleCloseChat}
                isConnected={isConnected}
                isMobile={true}
              />
              
              <ChatMessages
                messages={messages}
                isTyping={isTyping}
                onLoadMore={handleLoadMore}
                hasMoreMessages={hasMoreMessages}
                isLoading={isLoading}
              />
              
              <QuickActions
                onQuickAction={handleQuickAction}
                currentLesson={currentLesson}
              />
              
              <ChatInput
                onSendMessage={handleSendMessage}
                disabled={!isConnected}
                placeholder="Digite sua mensagem para Dai..."
              />
            </div>
          </div>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <div className="container mx-auto px-6 py-8">
            <Breadcrumb customItems={breadcrumbItems} />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h1 className="text-2xl font-heading font-semibold text-foreground mb-4">
                    Mentor Dai - Assistente de IA
                  </h1>
                  
                  <div className="space-y-4 text-card-foreground">
                    <p className="text-muted-foreground">
                      O Mentor Dai é seu assistente pessoal de aprendizagem, disponível 24/7 para ajudá-lo em sua jornada educacional.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-muted bg-opacity-30 rounded-lg">
                        <h3 className="font-heading font-medium mb-2">Explicações Personalizadas</h3>
                        <p className="text-sm text-muted-foreground">
                          Receba explicações adaptadas ao seu nível de conhecimento
                        </p>
                      </div>
                      
                      <div className="p-4 bg-muted bg-opacity-30 rounded-lg">
                        <h3 className="font-heading font-medium mb-2">Quizzes Interativos</h3>
                        <p className="text-sm text-muted-foreground">
                          Teste seus conhecimentos com quizzes personalizados
                        </p>
                      </div>
                      
                      <div className="p-4 bg-muted bg-opacity-30 rounded-lg">
                        <h3 className="font-heading font-medium mb-2">Conteúdo Relacionado</h3>
                        <p className="text-sm text-muted-foreground">
                          Descubra materiais complementares relevantes
                        </p>
                      </div>
                      
                      <div className="p-4 bg-muted bg-opacity-30 rounded-lg">
                        <h3 className="font-heading font-medium mb-2">Acompanhamento</h3>
                        <p className="text-sm text-muted-foreground">
                          Monitore seu progresso e identifique áreas de melhoria
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Tips */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Como usar o Mentor Dai
                  </h2>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-primary-foreground">1</span>
                      </div>
                      <p className="text-sm text-card-foreground">
                        Clique no botão flutuante no canto inferior direito para abrir o chat
                      </p>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-primary-foreground">2</span>
                      </div>
                      <p className="text-sm text-card-foreground">
                        Digite sua pergunta ou use as ações rápidas para interações comuns
                      </p>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-primary-foreground">3</span>
                      </div>
                      <p className="text-sm text-card-foreground">
                        O Dai fornecerá respostas contextualizadas baseadas no seu progresso atual
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">
                    Status da Sessão
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Conexão</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-success rounded-full" />
                        <span className="text-sm text-foreground">Online</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Mensagens hoje</span>
                      <span className="text-sm text-foreground">12</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Contexto atual</span>
                      <span className="text-sm text-foreground">JavaScript</span>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">
                    Perguntas Frequentes
                  </h3>
                  
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleSendMessage("Como posso melhorar meu desempenho nos cursos?")}
                      className="w-full text-left p-3 bg-muted bg-opacity-30 rounded-lg hover:bg-opacity-50 transition-micro"
                    >
                      <p className="text-sm text-foreground">Como melhorar meu desempenho?</p>
                    </button>
                    
                    <button 
                      onClick={() => handleSendMessage("Quais são os próximos tópicos recomendados?")}
                      className="w-full text-left p-3 bg-muted bg-opacity-30 rounded-lg hover:bg-opacity-50 transition-micro"
                    >
                      <p className="text-sm text-foreground">Próximos tópicos recomendados</p>
                    </button>
                    
                    <button 
                      onClick={() => handleSendMessage("Como funciona o sistema de avaliação?")}
                      className="w-full text-left p-3 bg-muted bg-opacity-30 rounded-lg hover:bg-opacity-50 transition-micro"
                    >
                      <p className="text-sm text-foreground">Sistema de avaliação</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Chat Panel */}
        {!isMobile && isChatOpen && (
          <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-card border border-border rounded-lg shadow-tier-3 z-300 backdrop-blur-modal">
            <div className="flex flex-col h-full">
              <ChatHeader 
                onClose={handleCloseChat}
                isConnected={isConnected}
                isMobile={false}
              />
              
              <ChatMessages
                messages={messages}
                isTyping={isTyping}
                onLoadMore={handleLoadMore}
                hasMoreMessages={hasMoreMessages}
                isLoading={isLoading}
              />
              
              <QuickActions
                onQuickAction={handleQuickAction}
                currentLesson={currentLesson}
              />
              
              <ChatInput
                onSendMessage={handleSendMessage}
                disabled={!isConnected}
                placeholder="Digite sua mensagem..."
              />
            </div>
          </div>
        )}

        {/* Floating Chat Button */}
        {(!isChatOpen || isMobile) && (
          <FloatingChatButton
            onClick={handleToggleChat}
            hasNotification={false}
            isOpen={isChatOpen}
          />
        )}
      </main>
    </div>
  );
};

export default AIChatInterface;